/**
 * GitHub Integration for Almadar
 * Provides git operations and GitHub API access for the agent
 */

import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';
import * as GitOps from './github-git';
import * as GitHubAPI from './github-api';
import type {
  GitHubCloneParams,
  GitHubCreateBranchParams,
  GitHubCommitParams,
  GitHubPushParams,
  GitHubCreatePRParams,
  GitHubGetPRCommentsParams,
  GitHubListIssuesParams,
  GitHubGetIssueParams,
} from './types';

/**
 * GitHub integration class
 */
export class GitHubIntegration extends BaseIntegration {
  private token: string;
  private owner: string;
  private repo: string;
  private workDir: string;

  constructor(config: IntegrationConfig) {
    super(config);

    this.token = config.env.GITHUB_TOKEN;
    if (!this.token) {
      throw new Error('GITHUB_TOKEN not configured');
    }

    this.owner = config.env.GITHUB_OWNER || '';
    this.repo = config.env.GITHUB_REPO || '';
    this.workDir = config.env.GITHUB_WORK_DIR || process.cwd();

    this.logger.info('GitHub integration initialized', {
      owner: this.owner,
      repo: this.repo,
    });
  }

  /**
   * Execute a GitHub action
   */
  async execute(
    action: string,
    params: IntegrationParams
  ): Promise<IntegrationResult> {
    // Validate params against registry
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();
    let retries = 0;

    try {
      let data: unknown;

      switch (action) {
        case 'cloneRepo':
          data = await this.executeWithRetry(() =>
            this.cloneRepo(params as GitHubCloneParams)
          );
          break;

        case 'createBranch':
          data = await this.executeWithRetry(() =>
            this.createBranch(params as GitHubCreateBranchParams)
          );
          break;

        case 'commit':
          data = await this.executeWithRetry(() =>
            this.commit(params as GitHubCommitParams)
          );
          break;

        case 'push':
          data = await this.executeWithRetry(() =>
            this.push(params as GitHubPushParams)
          );
          break;

        case 'createPR':
          data = await this.executeWithRetry(() =>
            this.createPR(params as GitHubCreatePRParams)
          );
          break;

        case 'getPRComments':
          data = await this.executeWithRetry(() =>
            this.getPRComments(params as GitHubGetPRCommentsParams)
          );
          break;

        case 'listIssues':
          data = await this.executeWithRetry(() =>
            this.listIssues(params as GitHubListIssuesParams)
          );
          break;

        case 'getIssue':
          data = await this.executeWithRetry(() =>
            this.getIssue(params as GitHubGetIssueParams)
          );
          break;

        default:
          throw new Error(`Unknown GitHub action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime, retries),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  /**
   * Clone a repository
   */
  private async cloneRepo(params: GitHubCloneParams): Promise<{ message: string }> {
    this.logger.debug('Cloning repository', { repoUrl: params.repoUrl });

    // If owner/repo not set, parse from URL
    if (!this.owner || !this.repo) {
      const parsed = GitHubAPI.parseRepoUrl(params.repoUrl);
      this.owner = parsed.owner;
      this.repo = parsed.repo;
    }

    await GitOps.cloneRepo(params, this.token);

    return {
      message: `Successfully cloned ${params.repoUrl} to ${params.targetDir}`,
    };
  }

  /**
   * Create a branch
   */
  private async createBranch(params: GitHubCreateBranchParams): Promise<{ message: string }> {
    this.logger.debug('Creating branch', { branchName: params.branchName });

    await GitOps.createBranch(params, this.workDir);

    return {
      message: `Successfully created branch: ${params.branchName}`,
    };
  }

  /**
   * Commit changes
   */
  private async commit(params: GitHubCommitParams): Promise<{ message: string }> {
    this.logger.debug('Committing changes', { message: params.message });

    await GitOps.commit(params, this.workDir);

    return {
      message: `Successfully committed changes: ${params.message}`,
    };
  }

  /**
   * Push branch
   */
  private async push(params: GitHubPushParams): Promise<{ message: string }> {
    this.logger.debug('Pushing branch', { branchName: params.branchName });

    await GitOps.push(params, this.workDir, this.token);

    return {
      message: `Successfully pushed branch: ${params.branchName}`,
    };
  }

  /**
   * Create a pull request
   */
  private async createPR(params: GitHubCreatePRParams) {
    this.logger.debug('Creating pull request', { title: params.title });

    const apiConfig = this.getAPIConfig();
    const pr = await GitHubAPI.createPR(params, apiConfig);

    this.logger.info('Pull request created', { number: pr.number, url: pr.url });

    return pr;
  }

  /**
   * Get PR comments
   */
  private async getPRComments(params: GitHubGetPRCommentsParams) {
    this.logger.debug('Getting PR comments', { prNumber: params.prNumber });

    const apiConfig = this.getAPIConfig();
    const comments = await GitHubAPI.getPRComments(params, apiConfig);

    return { comments };
  }

  /**
   * List issues
   */
  private async listIssues(params: GitHubListIssuesParams) {
    this.logger.debug('Listing issues', { state: params.state, labels: params.labels?.join(', '), limit: params.limit });

    const apiConfig = this.getAPIConfig();
    const issues = await GitHubAPI.listIssues(params, apiConfig);

    return { issues };
  }

  /**
   * Get issue details
   */
  private async getIssue(params: GitHubGetIssueParams) {
    this.logger.debug('Getting issue', { issueNumber: params.issueNumber });

    const apiConfig = this.getAPIConfig();
    const result = await GitHubAPI.getIssue(params, apiConfig);

    return result;
  }

  /**
   * Get API config for GitHub API calls
   */
  private getAPIConfig(): GitHubAPI.GitHubAPIConfig {
    if (!this.owner || !this.repo) {
      throw new Error(
        'GitHub owner and repo must be configured. Either set GITHUB_OWNER/GITHUB_REPO or clone a repository first.'
      );
    }

    return {
      token: this.token,
      owner: this.owner,
      repo: this.repo,
    };
  }
}

// Auto-register the integration
registerIntegration('github', GitHubIntegration);

// Export types
export * from './types';

// Export git CLI operations (clone, push, commit, branch)
export {
  cloneRepo,
  createBranch,
  commit,
  push,
  getCurrentBranch,
  hasUncommittedChanges,
  init,
  pull,
  addRemote,
  setUserConfig,
  getHeadSha,
  getHeadCommit,
  renameCurrentBranch,
} from './github-git';

// Export GitHub REST API functions
export {
  createRepo,
  listRepoTree,
  readRepoFile,
  createPR,
  getPRComments,
  listIssues,
  getIssue,
  getRateLimit,
  parseRepoUrl,
  listCommits,
  getCommitDiff,
  getFileAtCommit,
  type GitHubAPIConfig,
  type GitHubCommit,
} from './github-api';
